import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { isDefined } from 'remeda';
import { z } from 'zod/v4';

import { ContactPersonSchema } from './use-load-contact-person';
import {
  type ApiError,
  addResourceNameToQueryResult,
  handleZodError,
  useLegacyAxiosInstance,
} from './utils';

const ContactPersonsSchema = z.object({
  data: z.array(ContactPersonSchema),
});

export type ContactPersons = z.infer<typeof ContactPersonsSchema>;

export async function loadContactPersonsQueryFn(
  axiosInstance: Promise<AxiosInstance>,
  { queryKey }: any,
) {
  const instance = await axiosInstance;
  const { data } = await instance.get(`/debtors/${queryKey[1]}/contact_persons`);

  const parseResult = ContactPersonsSchema.safeParse(data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'contactPersons');
    throw parseResult.error;
  }

  return parseResult.data;
}

type LoadContactPersonVariables = {
  debtorId: number;
};

const emptyContactPersons: ContactPersons = { data: [] };

export function useLoadContactPersons({ debtorId }: LoadContactPersonVariables) {
  const axiosInstance = useLegacyAxiosInstance();

  const queryResult = useQuery<ContactPersons, ApiError>({
    queryKey: ['contact-persons', debtorId],
    queryFn: context => loadContactPersonsQueryFn(axiosInstance, context),
    placeholderData: previousData => {
      if (isDefined(previousData)) return previousData;
      return emptyContactPersons;
    },
  });

  return addResourceNameToQueryResult<
    ContactPersons,
    ApiError,
    'contactPersons',
    'PreventUndefined'
  >('contactPersons', queryResult, emptyContactPersons);
}
